/* eslint-disable react/prop-types */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import styled from 'styled-components';

import { mobile } from '../styles';
import { cropRatio, TALLEST } from './photoFit';

// The card look: booking, reservations and host venues (VenueLargeCard).
const CarouselImg = styled.img`
  max-height: 50vh;
  object-fit: cover;
  border-radius: 10px;
`;

// V5: the full-screen viewer. One stage that never changes size, so swiping
// does not jump. Each photo fits inside it whole (contain); the empty space
// is see-through, so it blends into the dark page behind.
//
// No Bootstrap here: its stylesheet comes from a CDN that no longer answers
// (AEO ledger, Backlog). The track is a native scroll-snap row, so a phone
// swipes it with its own momentum and no swipe code is needed.
const Stage = styled.div`
  position: relative;
  width: 90vw;
  height: 80vh;

  @media (max-width: ${mobile}) {
    width: 100vw;
    height: 75vh;
    /* svh ignores the phone address bar sliding in and out. */
    height: 75svh;
  }
`;

const Track = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }
`;

const Slide = styled.div`
  flex: 0 0 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  scroll-snap-align: center;
  scroll-snap-stop: always;
`;

const StageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  transition: opacity 0.2s ease-out;

  /* An extreme shape shows its centre part (photoFit.js). */
  ${({ $crop }) =>
    $crop &&
    `
    object-fit: cover;
    aspect-ratio: ${$crop};
    ${$crop === TALLEST ? 'width: auto; max-width: 100%;' : 'height: auto; max-height: 100%;'}
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Counter = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  pointer-events: none;
`;

const RoundButton = styled.button`
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  font-size: 28px;
  line-height: 44px;
  text-align: center;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    visibility: hidden;
  }
`;

const CloseButton = styled(RoundButton)`
  position: fixed;
  top: 8px;
  right: 8px;
  z-index: 1;
`;

const ArrowButton = styled(RoundButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => `${$side}: 8px;`}
`;

// The photo on screen and the one next to it (the one before, at the end).
// Nothing else is downloaded until the visitor gets near it.
const near = (i, total) => (i + 1 < total ? [i, i + 1] : [i, i - 1].filter((n) => n >= 0));

const FullScreen = ({ images, alts = [], defaultIndex = 0, onClose }) => {
  const total = images.length;
  const start = Math.min(Math.max(defaultIndex, 0), total - 1);
  const track = useRef(null);
  const [index, setIndex] = useState(start);
  const [loaded, setLoaded] = useState(() => new Set(near(start, total)));
  const [sizes, setSizes] = useState({});

  const show = (i) => {
    setIndex(i);
    setLoaded((prev) => (near(i, total).every((n) => prev.has(n)) ? prev : new Set([...prev, ...near(i, total)])));
  };

  // Only scroll. onScroll sets the counter, as it does for a swipe. If this
  // also set it, the first scroll events of the slide would set it back.
  const go = (i) => {
    if (i < 0 || i >= total) {
      return;
    }
    const el = track.current;
    if (el && el.scrollTo) {
      el.scrollTo({ left: i * el.clientWidth });
    }
  };

  // Open on the photo that was tapped, with no slide past the ones before it.
  useLayoutEffect(() => {
    const el = track.current;
    if (el) {
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = start * el.clientWidth;
      el.style.scrollBehavior = '';
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        go(index + 1);
      } else if (e.key === 'ArrowLeft') {
        go(index - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onScroll = (e) => {
    const { scrollLeft, clientWidth } = e.currentTarget;
    if (clientWidth) {
      const i = Math.min(Math.max(Math.round(scrollLeft / clientWidth), 0), total - 1);
      if (i !== index) {
        show(i);
      }
    }
  };

  return (
    <Stage>
      <CloseButton type="button" aria-label="Close photos" onClick={onClose}>
        <span aria-hidden="true">×</span>
      </CloseButton>
      <Track ref={track} onScroll={onScroll}>
        {images.map((url, i) => {
          const size = sizes[i];
          return (
            <Slide key={i}>
              {loaded.has(i) && (
                <StageImg
                  src={url}
                  alt={alts[i] || 'venue image'}
                  $ready={!!size}
                  $crop={size ? cropRatio(size.width, size.height) : null}
                  onLoad={(e) => {
                    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
                    setSizes((prev) => ({ ...prev, [i]: { width, height } }));
                  }}
                />
              )}
            </Slide>
          );
        })}
      </Track>
      {total > 1 && (
        <>
          <ArrowButton type="button" $side="left" aria-label="Previous" disabled={index === 0} onClick={() => go(index - 1)}>
            <span aria-hidden="true">‹</span>
          </ArrowButton>
          <ArrowButton type="button" $side="right" aria-label="Next" disabled={index === total - 1} onClick={() => go(index + 1)}>
            <span aria-hidden="true">›</span>
          </ArrowButton>
          <Counter>{`${index + 1} / ${total}`}</Counter>
        </>
      )}
    </Stage>
  );
};

const Card = ({ images, defaultIndex = 0 }) => {
  const [index, setIndex] = useState(defaultIndex);

  return (
    <Carousel activeIndex={index} onSelect={setIndex} className="carousel">
      {images.map((item) => {
        return (
          <Carousel.Item key={item}>
            <CarouselImg className="d-block w-100" src={item} alt="venue image" />
          </Carousel.Item>
        );
      })}
    </Carousel>
  );
};

const ImageCarousel = ({ fullScreen, ...props }) => (fullScreen ? <FullScreen {...props} /> : <Card {...props} />);

export default ImageCarousel;
